import {
    homeNode,
    IsNodeAlreadyComplete,
    todayNodeName,
    workflowRepository,
} from "motw_core/Repositories/WorkflowRepository";
import { GetAllVisibleNodesAsync } from "motw_core/Workflows/GetVisibleNodes";
import * as React from "react";
import { RenderAllNodes } from "source/app/presentation/MainPanel/Workflows/AllNodes/RenderAllNodes";
import { defaultState } from "source/app/presentation/MainPanel/Workflows/Helpers/defaultState";
import { EEventType } from "source/app/services/Events/EEventType";
import { DispatchEvent } from "source/app/services/Events/Events";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";
import { State } from "source/app/services/UI/State/State";
import Data from "../../../services/Data";
import { ESharedComponent } from "../../../services/UI/State/ESharedComponent";
import { GetWorkflowNodeFontStyle } from "./GetWorkflowNodeFontStyle";
import { IWorkflowsState } from "./IWorkflowsState";
import { IWorkflowNode } from "motw_core/Entity/IWorkflowNode";
import { SearchNodes } from "./SearchNodes/SearchNodes";
import { LoadingSpinner } from "../Calendar/LoadingSpinner";
import { ESharedFunction } from "motw_core/Enums/ESharedFunction";
import { SortFavoriteNodesByLastModifiedTime } from "./Helpers/SortFavoriteNodesByLastModifiedTime";

export class Workflows extends React.Component<
    IWorkflowsProps,
    IWorkflowsState
> {
    private isMounted = false;
    private hasStartedLoading = false;
    private readonly handleFontSwitcherChanged = () => this.forceUpdate();

    constructor(props: IWorkflowsProps) {
        super(props);
        this.state = defaultState;
    }

    async componentDidMount() {
        this.isMounted = true;
        State.ShareClass(ESharedComponent.Workflows, this);
        document.addEventListener(
            ESharedFunction[ESharedFunction.FontSwitcher_useAnopenscrollFont],
            this.handleFontSwitcherChanged
        );

        if (this.props.isPanelActive !== false) {
            await this.LoadDataAsync();
        }
    }

    componentWillUnmount() {
        this.isMounted = false;
        Data.Unload(this);
        document.removeEventListener(
            ESharedFunction[ESharedFunction.FontSwitcher_useAnopenscrollFont],
            this.handleFontSwitcherChanged
        );
    }

    async componentDidUpdate(
        prevProps: IWorkflowsProps,
        prevState: IWorkflowsState
    ) {
        if (
            prevProps.isPanelActive === false &&
            this.props.isPanelActive !== false
        ) {
            await this.LoadDataAsync();
        }

        if (!this.state.rootNode) {
            this.setState({ rootNode: homeNode });
        }

        if (prevState !== this.state) {
            DispatchEvent(EEventType.Workflows_DataChanged);
        }
        State.ShareClass(ESharedComponent.Workflows, this);
    }

    private async LoadDataAsync() {
        if (this.hasStartedLoading) {
            return;
        }

        this.hasStartedLoading = true;

        await Data.Load.Component({
            component: this,
            load: async () => LoadWorkflowsDataAsync(() => this.isMounted),
            inLoadAll: true,
        });

        if (this.isMounted) {
            DispatchEvent(EEventType.Workflows_Mounted);
        }
    }

    render() {
        if (this.props.isPanelActive === false) {
            return null;
        }

        if (this.state.isLoading) {
            return <LoadingSpinner />;
        }

        if (!this.state.visibleNodes.length) {
            return null;
        }

        const fontStyle = GetWorkflowNodeFontStyle(this.state.rootNode);

        return (
            <div style={{ ...fontStyle }}>
                <RenderAllNodes {...{ state: this.state }} />
                <SearchNodes
                    rootNode={this.state.rootNode}
                    favoriteNodes={this.state.favoriteNodes}
                />
            </div>
        );
    }
}

export default Workflows;

export function GetWorkflowsState() {
    const workflows = State.ReadClass<IWorkflowsState>(
        ESharedComponent.Workflows
    );

    return workflows;
}

interface IWorkflowsProps {
    isPanelActive?: boolean;
}

export async function LoadWorkflowsDataAsync(
    isMounted: () => boolean = () => true
) {
    const workflows = GetWorkflowsState();

    if (!workflows) {
        return;
    }

    if (!workflows.state.rootNode) {
        return;
    }

    const [visibleNodesData, favoriteNodesData, todayNode] = await Promise.all([
        GetAllVisibleNodesAsync(workflows.state.rootNode),
        workflowRepository.GetFavoriteNodes(ClientDataProvider),
        workflowRepository.GetNodeByNameAsync(ClientDataProvider, todayNodeName),
    ]);
    const visibleNodes: IWorkflowNode[] = visibleNodesData ?? [];

    const rootNode = visibleNodes.find(
        (node) => node.id === workflows.state.rootNode.id
    );

    const favoriteNodes = SortFavoriteNodesByLastModifiedTime(
        Array.isArray(favoriteNodesData) ? favoriteNodesData : []
    );

    const todayNodes = todayNode
        ? await GetAllVisibleNodesAsync(todayNode).then((nodes: IWorkflowNode[]) =>
              nodes.filter(
                  (node) =>
                      node.id !== todayNode.id &&
                      !IsNodeAlreadyComplete(node.name)
              )
          )
        : [];

    if (!isMounted()) {
        return;
    }

    workflows.setState({
        visibleNodes,
        rootNode: rootNode ?? workflows.state.rootNode,
        favoriteNodes,
        todayNodes,
        selectedNodeIds: [],
        isLoading: false,
    });
}
